import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import CropCard from '../../components/crops/CropCard';
import BackButton from '../../components/common/BackButton';

const STAR_NAMES = ['Beginner','Active Farmer','Growing Farmer','Influencer Farmer','Leader Farmer','Top Farmer','Elite Farmer'];

export default function FarmerProfilePage() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { user: me } = useAuth();
  const [farmer, setFarmer]           = useState(null);
  const [crops, setCrops]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    try {
      const { data } = await api.get(`/users/${id}`);
      setFarmer(data.user);
      setCrops(data.crops || []);
    } catch {
      toast.error('Profile not found');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!me) return navigate('/login');
    setFollowLoading(true);
    try {
      const { data } = await api.post(`/users/${id}/follow`);
      setFarmer(p => ({ ...p, isFollowing: data.following, followerCount: data.followerCount }));
      toast.success(data.following ? 'Following!' : 'Unfollowed');
    } catch { toast.error('Error'); }
    finally { setFollowLoading(false); }
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'70vh' }}>
      <div className="spinner" style={{ width:36, height:36 }} />
    </div>
  );
  if (!farmer) return null;

  const starLevel = farmer.starLevel || 0;
  const starName  = STAR_NAMES[Math.min(starLevel, 6)];
  const stars     = starLevel >= 6 ? '🥇' : '★'.repeat(starLevel) + '☆'.repeat(Math.max(0, 5 - starLevel));
  const isMe      = me?._id === id;

  // ── Call button logic ──────────────────────────────────────
  // Show call only if:
  // - Viewer is buyer or agent (NOT seller/farmer)
  // - Not viewing own profile
  // - Farmer has a mobile number
  const canCall = !isMe
    && (me?.role === 'buyer' || me?.role === 'agent')
    && farmer?.mobile;

  // Use farmer's registered mobile number
  const farmerMobile = farmer?.mobile;

  // Theme color based on farmer's module/role
  const themeColor = '#1A6B3C';

  return (
    <div style={{ paddingBottom: 90 }}>

      {/* ── Header with theme color ── */}
      <div style={{
        background: `linear-gradient(135deg, ${themeColor} 0%, #2d9e5f 100%)`,
        padding:'16px 20px',
        display:'flex', alignItems:'center', gap:12
      }}>
        <BackButton />
        <h1 style={{ color:'white', fontSize:18, fontWeight:600, flex:1 }}>Farmer Profile</h1>
        {/* Star badge in header */}
        <div style={{ background:'rgba(255,255,255,0.2)', borderRadius:20, padding:'4px 10px', fontSize:12, color:'white' }}>
          {starLevel >= 6 ? '🥇' : `⭐ ${starName}`}
        </div>
      </div>

      {/* ── Banner with gradient ── */}
      <div style={{
        background: `linear-gradient(135deg, ${themeColor} 0%, #2d9e5f 100%)`,
        height: 70,
      }} />

      <div style={{ maxWidth:480, margin:'0 auto', padding:'0 16px' }}>

        {/* ── Profile card ── */}
        <div className="card" style={{ padding:20, marginTop:-40, marginBottom:16, textAlign:'center' }}>

          {/* Avatar */}
          <div style={{
            width:76, height:76, borderRadius:'50%',
            background: themeColor,
            margin:'0 auto 10px',
            display:'flex', alignItems:'center', justifyContent:'center',
            color:'white', fontWeight:700, fontSize:28,
            border:'4px solid white',
            boxShadow:'0 4px 12px rgba(0,0,0,0.15)',
            overflow:'hidden',
          }}>
            {farmer.profileImageUrl
              ? <img src={farmer.profileImageUrl} alt="" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }} />
              : (farmer.name?.[0]?.toUpperCase() || 'F')
            }
          </div>

          <h2 style={{ fontSize:18, fontWeight:700, marginBottom:2 }}>{farmer.name || 'Farmer'}</h2>
          <p style={{ color:'#6B7280', fontSize:13, marginBottom:6 }}>
             {farmer.locationAddress || 'Location not set'}
          </p>
          <div style={{ fontSize:15, color:'#F59E0B', marginBottom:12 }}>
            {stars} <span style={{ color:'#6B7280', fontSize:12 }}>{starName}</span>
          </div>

          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', borderTop:'1px solid #F3F4F6', paddingTop:14 }}>
            {[
              { label:'Crops',     value: farmer.cropCount     || 0 },
              { label:'Followers', value: farmer.followerCount || 0 },
              { label:'Following', value: farmer.followingCount|| 0 },
            ].map((s, i) => (
              <div key={s.label} style={{
                textAlign:'center',
                borderRight: i < 2 ? '1px solid #F3F4F6' : 'none',
                padding:'0 8px'
              }}>
                <div style={{ fontSize:20, fontWeight:700, color: themeColor }}>{s.value}</div>
                <div style={{ fontSize:12, color:'#9CA3AF' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* ── Action buttons ── */}
          {!isMe && (
            <div style={{ display:'flex', gap:8, marginTop:16 }}>
              {/* Follow button — everyone can follow */}
              <button
                onClick={handleFollow}
                disabled={followLoading}
                style={{
                  flex:1, padding:'10px',
                  background: farmer.isFollowing ? '#F3F4F6' : themeColor,
                  color: farmer.isFollowing ? '#6B7280' : 'white',
                  border: farmer.isFollowing ? '1px solid #E5E7EB' : 'none',
                  borderRadius:10, fontSize:13, fontWeight:600,
                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                }}
              >
                {followLoading
                  ? <span className="spinner" style={{ width:16, height:16, borderTopColor: farmer.isFollowing ? '#6B7280' : 'white' }} />
                  : farmer.isFollowing ? '✓ Following' : '+ Follow'
                }
              </button>

              {/* Call button — ONLY buyers and agents can call farmers */}
              {canCall && (
                <a
                  href={`tel:+91${farmerMobile}`}
                  style={{
                    flex:1, padding:'10px',
                    background:'#EFF6FF',
                    color:'#2563EB',
                    border:'1px solid #BFDBFE',
                    borderRadius:10, fontSize:13, fontWeight:600,
                    cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                    textDecoration:'none',
                  }}
                >
                  📞 Call
                </a>
              )}

              {/* Mobile hint for farmers viewing farmers */}
              {!canCall && !isMe && me?.role === 'seller' && (
                <div style={{
                  flex:1, padding:'10px',
                  background:'#F9FAFB',
                  color:'#9CA3AF',
                  border:'1px solid #E5E7EB',
                  borderRadius:10, fontSize:12,
                  display:'flex', alignItems:'center', justifyContent:'center', gap:4,
                }}>
                  👨‍🌾 Farmer
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Ready to sell crops ── */}
        {crops.filter(c => c.status === 'ready_to_sell').length > 0 && (
          <div style={{ marginBottom:16 }}>
            <h3 style={{ fontSize:15, fontWeight:600, marginBottom:10, color: themeColor }}>
              🟢 Ready to Sell
            </h3>
            {crops.filter(c => c.status === 'ready_to_sell').map(crop => (
              <CropCard key={crop._id} crop={crop} showFarmer={false} />
            ))}
          </div>
        )}

        {/* ── All crops ── */}
        {crops.filter(c => c.status !== 'ready_to_sell').length > 0 && (
          <div>
            <h3 style={{ fontSize:15, fontWeight:600, marginBottom:10 }}> All Crops</h3>
            {crops.filter(c => c.status !== 'ready_to_sell').map(crop => (
              <CropCard key={crop._id} crop={crop} showFarmer={false} />
            ))}
          </div>
        )}

        {crops.length === 0 && (
          <div style={{ textAlign:'center', padding:40, color:'#9CA3AF' }}>
            <div style={{ fontSize:40, marginBottom:8 }}></div>
            <p>No crops posted yet</p>
          </div>
        )}
      </div>
    </div>
  );
}